import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { Bug, Sparkles, HelpCircle, MoreHorizontal, ExternalLink, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

const CATEGORY_META = {
  bug:      { label: 'Bug',     cls: 'bg-red-100 text-red-700 border-red-200',       icon: Bug },
  feature:  { label: 'Feature', cls: 'bg-violet-100 text-violet-700 border-violet-200', icon: Sparkles },
  question: { label: 'Question',cls: 'bg-blue-100 text-blue-700 border-blue-200',    icon: HelpCircle },
  other:    { label: 'Other',   cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: MoreHorizontal },
};

const STATUS_META = {
  open:         { label: 'Open',         cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  acknowledged: { label: 'Acknowledged', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  resolved:     { label: 'Resolved',     cls: 'bg-green-100 text-green-700 border-green-200' },
};

const ROLE_LABEL = { super_user: 'Super Admin', admin: 'Admin', office_staff: 'Office Staff' };

function StatusDropdown({ issue, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const current = STATUS_META[issue.status] || STATUS_META.open;

  const select = async (status) => {
    if (status === issue.status) { setOpen(false); return; }
    setLoading(true);
    setOpen(false);
    try { await onUpdate(issue.id, status); }
    finally { setLoading(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        disabled={loading}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-opacity ${current.cls} ${loading ? 'opacity-50' : 'hover:opacity-80'}`}
      >
        {loading
          ? <RefreshCw className="w-3 h-3 animate-spin" />
          : <>{current.label} <ChevronDown className="w-3 h-3" /></>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <button
                key={key}
                onClick={(e) => { e.stopPropagation(); select(key); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 transition-colors
                  ${key === issue.status ? 'font-semibold text-slate-800' : 'text-slate-600'}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${key === 'open' ? 'bg-amber-400' : key === 'acknowledged' ? 'bg-blue-400' : 'bg-green-400'}`} />
                {meta.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function IssueRow({ issue, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const cat  = CATEGORY_META[issue.category] || CATEGORY_META.other;
  const CatIcon = cat.icon;

  return (
    <>
      <tr
        className={`border-b border-slate-100 transition-colors cursor-pointer ${expanded ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
        onClick={() => setExpanded(e => !e)}
      >
        <td className="px-4 py-3 w-8 text-slate-400">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </td>
        <td className="px-4 py-3">
          <p className="text-sm font-medium text-slate-800">{issue.title}</p>
        </td>
        <td className="px-4 py-3 hidden sm:table-cell">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cat.cls}`}>
            <CatIcon className="w-3 h-3" />
            {cat.label}
          </span>
        </td>
        <td className="px-4 py-3 hidden md:table-cell">
          <p className="text-xs text-slate-700 font-medium">{issue.reported_by}</p>
          <p className="text-xs text-slate-400">{ROLE_LABEL[issue.reporter_role] || issue.reporter_role}</p>
        </td>
        <td className="px-4 py-3 hidden lg:table-cell">
          <p className="text-xs text-slate-500">
            {new Date(issue.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
          <p className="text-xs text-slate-400">
            {new Date(issue.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </td>
        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
          <StatusDropdown issue={issue} onUpdate={onUpdate} />
        </td>
        <td className="px-4 py-3 hidden lg:table-cell text-right" onClick={e => e.stopPropagation()}>
          {issue.github_issue_url
            ? <a href={issue.github_issue_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors">
                #{issue.github_issue_number} <ExternalLink className="w-3 h-3" />
              </a>
            : <span className="text-xs text-slate-300">—</span>}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50 border-b border-slate-100">
          <td colSpan={7} className="px-6 pb-4 pt-2">
            <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed max-w-3xl">
              {issue.body}
            </div>
            {issue.github_issue_url && (
              <a href={issue.github_issue_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-600 hover:text-blue-800 transition-colors">
                <ExternalLink className="w-3.5 h-3.5" />
                View on GitHub — Issue #{issue.github_issue_number}
              </a>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function ReportedIssuesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_user') { navigate('/'); }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getReportedIssues({
        status:   statusFilter   || undefined,
        category: categoryFilter || undefined,
      });
      setIssues(data);
    } catch (e) {
      setError(e.message || 'Failed to load issues.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const handleStatusUpdate = async (id, status) => {
    await apiService.updateIssueStatus(id, status);
    setIssues(prev => prev.map(i => i.id === id ? { ...i, status, updated_at: new Date().toISOString() } : i));
  };

  const counts = {
    open:         issues.filter(i => i.status === 'open').length,
    acknowledged: issues.filter(i => i.status === 'acknowledged').length,
    resolved:     issues.filter(i => i.status === 'resolved').length,
  };

  if (user?.role !== 'super_user') return null;

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Reported Issues</h1>
        <p className="text-slate-500 text-sm">In-app issue reports submitted by staff — visible to super admins only</p>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3 mb-6">
        {[
          { key: 'open',         label: 'Open',         count: counts.open,         color: 'amber' },
          { key: 'acknowledged', label: 'Acknowledged', count: counts.acknowledged, color: 'blue' },
          { key: 'resolved',     label: 'Resolved',     count: counts.resolved,     color: 'green' },
        ].map(({ key, label, count, color }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(f => f === key ? '' : key)}
            className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all
              ${statusFilter === key
                ? color === 'amber' ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                  : color === 'blue' ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                  : 'bg-green-500 text-white border-green-500 shadow-sm'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
          >
            <span className={`font-bold mr-1.5
              ${statusFilter !== key
                ? color === 'amber' ? 'text-amber-600'
                  : color === 'blue' ? 'text-blue-600'
                  : 'text-green-600'
                : 'text-white'}`}>{count}</span>
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
        >
          <option value="">All Categories</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature Request</option>
          <option value="question">Question</option>
          <option value="other">Other</option>
        </select>
        <span className="ml-auto text-sm text-slate-400 self-center">{issues.length} issue{issues.length !== 1 ? 's' : ''}</span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-400">Loading issues…</p>
          </div>
        ) : issues.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <Bug className="w-10 h-10 text-slate-200" />
            <p className="text-sm text-slate-400">No issues found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="w-8 px-4 py-3" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Reporter</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">GitHub</th>
                </tr>
              </thead>
              <tbody>
                {issues.map(issue => (
                  <IssueRow key={issue.id} issue={issue} onUpdate={handleStatusUpdate} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
